import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  ShieldAlert,
  Settings,
  Wand2,
  LogOut,
  Loader2,
  TrendingUp,
  Users,
  DollarSign,
  Image as ImageIcon,
  ExternalLink,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { AdminAssistant } from "@/components/admin/AdminAssistant";

const TABS = [
  { id: "analytics", label: "Analytics", icon: LayoutDashboard },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "screening", label: "Screening Queue", icon: ShieldAlert },
  { id: "pricing", label: "Pricing & Config", icon: Settings },
  { id: "studio", label: "Test Studio", icon: Wand2 },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    http
      .get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  async function logout() {
    try {
      await http.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    nav("/admin/login");
  }

  if (!authed)
    return (
      <div className="grid min-h-screen place-items-center bg-[#F4F3F6]">
        <Loader2 className="animate-spin text-[#9B5DE5]" size={40} />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F4F3F6]" style={{ fontFamily: "Outfit, sans-serif" }}>
      <div className="mx-auto flex max-w-7xl flex-col md:flex-row">
        {/* sidebar */}
        <aside className="w-full shrink-0 border-b border-black/5 bg-white p-4 md:min-h-screen md:w-60 md:border-b-0 md:border-r">
          <h2 className="px-2 py-3 text-lg font-bold text-[#4A235A]">Legend Admin</h2>
          <nav className="flex gap-1 overflow-x-auto md:flex-col">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-[#4A235A] text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <t.icon size={18} /> {t.label}
              </button>
            ))}
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"
            >
              <LogOut size={18} /> Logout
            </button>
          </nav>
        </aside>

        <main className="flex-1 p-5 sm:p-8">
          {tab === "analytics" && <Analytics />}
          {tab === "orders" && <Orders />}
          {tab === "screening" && <Screening />}
          {tab === "pricing" && <Pricing />}
          {tab === "studio" && <TestStudio />}
        </main>
      </div>
      <AdminAssistant />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-[#1A0F1C]">{title}</h1>
      {children}
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get("/admin/analytics").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!data) return <Loader2 className="animate-spin text-[#9B5DE5]" />;
  const cards = [
    { label: "Revenue", value: `$${data.revenue.toFixed(2)}`, icon: DollarSign },
    { label: "Paid Orders", value: data.paid_orders, icon: ShoppingBag },
    { label: "Characters Created", value: data.total_creations, icon: Users },
    { label: "Previews Generated", value: data.total_previews, icon: ImageIcon },
    { label: "Conversion", value: `${data.conversion}%`, icon: TrendingUp },
    { label: "Rejected Uploads", value: data.rejected_uploads, icon: ShieldAlert },
  ];
  return (
    <Section title="Analytics">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-black/5 bg-white p-5" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">{c.label}</span>
              <c.icon size={18} className="text-[#9B5DE5]" />
            </div>
            <p className="mt-2 text-3xl font-bold text-[#4A235A]">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl border border-black/5 bg-white p-5">
        <h3 className="mb-3 font-bold text-[#4A235A]">Paid orders by package</h3>
        {Object.keys(data.orders_by_package).length === 0 && <p className="text-sm text-gray-500">No paid orders yet.</p>}
        {Object.entries(data.orders_by_package).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between border-b border-gray-100 py-2 text-sm">
            <span className="font-semibold capitalize text-gray-700">{k}</span>
            <span className="font-bold text-[#4A235A]">{v}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (loading) return <Loader2 className="animate-spin text-[#9B5DE5]" />;
  return (
    <Section title="Orders">
      <div className="overflow-x-auto rounded-xl border border-black/5 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="p-3">Order</th>
              <th className="p-3">Package</th>
              <th className="p-3">Email</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3">Date</th>
            </tr>
          </thead>
          <tbody data-testid="admin-orders-table">
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-gray-100">
                <td className="p-3 font-mono font-bold text-[#4A235A]">{o.id}</td>
                <td className="p-3">{o.package_name}</td>
                <td className="p-3 text-gray-600">{o.email}</td>
                <td className="p-3">${Number(o.amount).toFixed(2)}</td>
                <td className="p-3">
                  <span className={`rounded-full px-2 py-1 text-xs font-bold ${o.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="p-3 text-gray-500">{new Date(o.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-gray-500">No orders yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Screening() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/admin/screening-queue").then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (loading) return <Loader2 className="animate-spin text-[#9B5DE5]" />;
  return (
    <Section title="Screening Queue (rejected uploads)">
      <div className="space-y-3" data-testid="screening-queue">
        {logs.map((l) => (
          <div key={l.id} className="rounded-xl border border-black/5 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-red-600">Rejected</span>
              <span className="text-xs text-gray-500">{new Date(l.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-2 text-sm text-gray-700">{l.reason || "No reason recorded."}</p>
            <p className="mt-1 text-xs text-gray-400">Confidence: {l.confidence}</p>
          </div>
        ))}
        {logs.length === 0 && <p className="text-sm text-gray-500">No rejected uploads. Screening is clean.</p>}
      </div>
    </Section>
  );
}

const MODEL_PRESETS = {
  fast: { image_model: "gpt-image-2", vision_model: "gpt-5.6-sol", vision_fallback_model: "gpt-5.6-sol", text_model: "gpt-5.6-sol" },
  best: { image_model: "gpt-image-2", vision_model: "gpt-5.6-luna", vision_fallback_model: "gpt-5.6-sol", text_model: "gpt-5.6-luna" },
};

function Pricing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    http.get("/admin/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!cfg) return <Loader2 className="animate-spin text-[#9B5DE5]" />;

  function setPkg(i, field, val) {
    const packages = cfg.packages.map((p, idx) => (idx === i ? { ...p, [field]: val } : p));
    setCfg({ ...cfg, packages });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        preview_cap: Number(cfg.preview_cap),
        download_expiry_days: Number(cfg.download_expiry_days),
        image_model: cfg.image_model,
        vision_model: cfg.vision_model,
        vision_fallback_model: cfg.vision_fallback_model,
        text_model: cfg.text_model,
        packages: cfg.packages.map((p) => ({ ...p, price: Number(p.price) })),
      };
      const { data } = await http.put("/admin/config", payload);
      setCfg(data);
      toast.success("Config saved & Stripe pricing synced.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Pricing & Config">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Preview cap">
          <input type="number" value={cfg.preview_cap} onChange={(e) => setCfg({ ...cfg, preview_cap: e.target.value })} data-testid="cfg-preview-cap" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
        <Field label="Download expiry (days)">
          <input type="number" value={cfg.download_expiry_days} onChange={(e) => setCfg({ ...cfg, download_expiry_days: e.target.value })} data-testid="cfg-expiry" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
        <Field label="Image model (generation)">
          <input value={cfg.image_model} onChange={(e) => setCfg({ ...cfg, image_model: e.target.value })} data-testid="cfg-image-model" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
        <Field label="Vision + prompt model">
          <input value={cfg.vision_model || ""} onChange={(e) => setCfg({ ...cfg, vision_model: e.target.value })} data-testid="cfg-vision-model" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
        <Field label="Vision fallback (low-effort reasoning)">
          <input value={cfg.vision_fallback_model || ""} onChange={(e) => setCfg({ ...cfg, vision_fallback_model: e.target.value })} data-testid="cfg-vision-fallback" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
        <Field label="Text / story model">
          <input value={cfg.text_model || ""} onChange={(e) => setCfg({ ...cfg, text_model: e.target.value })} data-testid="cfg-text-model" className="w-full rounded-lg border border-gray-200 p-2" />
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-gray-600">Quality presets:</span>
        <button
          type="button"
          data-testid="preset-fast"
          onClick={() => setCfg({ ...cfg, ...MODEL_PRESETS.fast })}
          className="rounded-full border border-[#4A235A]/20 px-4 py-2 text-sm font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white"
        >
          ⚡ Fast
        </button>
        <button
          type="button"
          data-testid="preset-best"
          onClick={() => setCfg({ ...cfg, ...MODEL_PRESETS.best })}
          className="rounded-full border border-[#4A235A]/20 px-4 py-2 text-sm font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white"
        >
          ✨ Best
        </button>
        <span className="text-xs text-gray-400">Sets all four models at once — remember to Save.</span>
      </div>

      <h3 className="mb-3 mt-8 font-bold text-[#4A235A]">Package prices (USD)</h3>
      <div className="space-y-3">
        {cfg.packages.map((p, i) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border border-black/5 bg-white p-4">
            <div className="flex-1">
              <p className="font-bold text-[#4A235A]">{p.name}</p>
              <p className="text-xs text-gray-500">{p.description}</p>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold text-gray-400">$</span>
              <input
                type="number"
                value={p.price}
                onChange={(e) => setPkg(i, "price", e.target.value)}
                data-testid={`cfg-price-${p.id}`}
                className="w-24 rounded-lg border border-gray-200 p-2 font-bold"
              />
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="cfg-save"
        className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-[#4A235A] px-6 font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Settings size={18} />} Save & sync Stripe
      </button>
    </Section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-gray-600">{label}</label>
      {children}
    </div>
  );
}

const DELIVERABLES = {
  portrait: ["Clean high-res portrait image", "Print-ready poster (all sizes)"],
  guardian: ["Everything in Portrait", "Personality, backstory & tagline"],
  epic: ["Everything in Guardian", "Dramatic action-scene image"],
  bundle: ["Everything in Epic", "+2 bonus character credits (real purchases)"],
};

const MODELS = [
  { id: "gpt-image-2", label: "gpt-image-2" },
  { id: "gpt-image-1", label: "gpt-image-1" },
  { id: "both", label: "Compare both" },
];

function TestStudio() {
  const fileRef = useRef();
  const [form, setForm] = useState({
    name: "Testarion the Bold",
    type: "dragon",
    personality: "brave and playful",
    role: "guardian of the village",
    appearance: "purple scales, golden wings",
    powers: "rainbow fire",
    description: "a child's crayon drawing of a friendly red dragon",
  });
  const [model, setModel] = useState("gpt-image-2");
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unlocking, setUnlocking] = useState(null);
  const [results, setResults] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [describing, setDescribing] = useState(false);
  const [verdict, setVerdict] = useState(null);

  useEffect(() => {
    http.get("/config").then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, []);

  async function handleDrawing(file) {
    if (!file) return;
    setDrawing(URL.createObjectURL(file));
    setDescribing(true);
    setVerdict(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/admin/studio/describe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, description: data.description || f.description }));
      setVerdict({ ok: data.is_drawing, reason: data.reason });
      toast.success("Drawing analysed — description filled in from your artwork.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDescribing(false);
    }
  }

  async function generate() {
    setBusy(true);
    setResults([]);
    const models = model === "both" ? ["gpt-image-1", "gpt-image-2"] : [model];
    try {
      const out = await Promise.all(
        models.map((m) => http.post("/admin/studio/generate", { ...form, image_model: m }).then((r) => r.data))
      );
      setResults(out);
      toast.success(model === "both" ? "Generated with both models — compare below." : "Generated free — unlock any package below.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function unlock(res, pkg) {
    const key = `${res.creation_id}:${pkg.id}`;
    setUnlocking(key);
    try {
      const { data } = await http.post("/admin/studio/unlock", {
        creation_id: res.creation_id,
        generation_id: res.generation_id,
        package_id: pkg.id,
      });
      toast.success(`${pkg.name} unlocked free — opening deliverables…`);
      window.open(`/download/${data.download_token}`, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUnlocking(null);
    }
  }

  return (
    <Section title="Test Studio">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          data-testid="studio-home-link"
          className="inline-flex items-center gap-2 rounded-xl border border-[#4A235A]/20 px-4 py-2 text-sm font-bold text-[#4A235A] hover:bg-[#4A235A]/5"
        >
          <ExternalLink size={16} /> Open the live site (home)
        </a>
        <span className="text-xs text-gray-400">Tip: while you're logged in as admin, the normal site downloads are free for you too.</span>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-gray-500">
        Generate a character and unlock <strong>any of the 4 packages for free, unlimited times</strong> — no payment,
        no preview cap, no screening. Everything here is excluded from live analytics and orders.
      </p>

      {/* drawing upload */}
      <div className="mb-5 flex flex-wrap items-center gap-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="studio-drawing-input"
          onChange={(e) => handleDrawing(e.target.files?.[0])}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={describing}
          data-testid="studio-upload-drawing"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#4A235A]/20 px-4 text-sm font-bold text-[#4A235A] hover:bg-[#4A235A]/5 disabled:opacity-60"
        >
          {describing ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
          {drawing ? "Use a different drawing" : "Upload a real drawing (optional)"}
        </button>
        {drawing && <img src={drawing} alt="uploaded drawing" className="h-16 w-16 rounded-lg border object-cover" />}
        {verdict && (
          <span className={`text-xs font-semibold ${verdict.ok ? "text-green-600" : "text-amber-600"}`}>
            {verdict.ok ? "Detected as a drawing" : `Would be rejected publicly: ${verdict.reason}`}
          </span>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:max-w-3xl">
        {["name", "type", "personality", "role", "appearance", "powers", "description"].map((k) => (
          <input
            key={k}
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            placeholder={k}
            data-testid={`studio-${k}`}
            className="rounded-lg border border-gray-200 p-3 text-sm"
          />
        ))}
      </div>

      {/* model toggle */}
      <div className="mt-5">
        <p className="mb-2 text-sm font-semibold text-gray-600">Image model</p>
        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1">
          {MODELS.map((m) => (
            <button
              key={m.id}
              onClick={() => setModel(m.id)}
              data-testid={`studio-model-${m.id}`}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
                model === m.id ? "bg-[#4A235A] text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={generate}
        disabled={busy}
        data-testid="studio-generate"
        className="mt-4 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[#4A235A] px-6 font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        {busy ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
        {busy ? "Generating…" : model === "both" ? "Generate & compare both" : "Generate character (free)"}
      </button>

      {results.length > 0 && (
        <div className={`mt-8 grid gap-6 ${results.length > 1 ? "lg:grid-cols-2" : "lg:max-w-4xl lg:grid-cols-2"}`}>
          {results.map((res) => (
            <div key={res.creation_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`studio-result-${res.image_model}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded-full bg-[#4A235A]/10 px-3 py-1 text-xs font-extrabold text-[#4A235A]">{res.image_model}</span>
              </div>
              <img
                src={`data:image/png;base64,${res.preview_b64}`}
                alt="studio preview"
                className="w-full rounded-xl shadow"
                data-testid="studio-preview"
              />
              {res.story?.tagline && (
                <p className="mt-3 font-display text-base font-bold italic text-[#9B5DE5]">“{res.story.tagline}”</p>
              )}
              <p className="mb-2 mt-4 text-sm font-bold text-[#4A235A]">Unlock & verify a package (free)</p>
              <div className="grid gap-2">
                {packages.map((p) => {
                  const key = `${res.creation_id}:${p.id}`;
                  return (
                    <div key={p.id} className="rounded-xl border border-[#4A235A]/15 p-3">
                      <button
                        onClick={() => unlock(res, p)}
                        disabled={unlocking === key}
                        data-testid={`studio-unlock-${p.id}`}
                        className="flex w-full items-center justify-between gap-3 text-left text-sm font-bold text-[#4A235A]"
                      >
                        <span>{p.name} · <span className="opacity-60">${Number(p.price).toFixed(0)} value</span></span>
                        {unlocking === key ? <Loader2 className="animate-spin" size={16} /> : <ExternalLink size={16} className="text-[#9B5DE5]" />}
                      </button>
                      <ul className="mt-1.5 space-y-0.5">
                        {(DELIVERABLES[p.id] || []).map((d) => (
                          <li key={d} className="text-xs text-gray-500">• {d}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
